/**
 * "Add into the current project" (W2-09 publish-import; DESIGN.md §5.12 step 3): an imported chart's lines, notes,
 * tracks, groups and the files they use are added to the open project at a beat, as ONE undoable step
 * (Core.merge.mergeChart). Identical tempo maps merge by beats; otherwise items go through the song time into this
 * chart's beats (exact where possible, else the nearest 1/1920, counted in the command's notes). A version is kept
 * before the merge (DESIGN.md §5.13 "versions before import"); the files the merged lines use are stored first
 * (content-addressed, so a file the project already has is not stored twice).
 *
 * Placement (`MergeAlign`, the merge command's `align`): automatic, by beats or by song time; the report dialog
 * returns the choice (ImportReportResult.align) and remembers it in `settings.ext['import.mergeAlign']`.
 */

import type { AppServices } from '../../app/registry/types';
import type { SettingsService } from '../../app/settings/types';
import { Core } from '../../core/commands/index';
import { MERGE_ALIGNS, type MergeAlign } from '../../core/commands/merge';
import type { AssetId, ChartDoc } from '../../core/model/types';
import type { Beat } from '../../core/time/types';
import type { MessageRef } from '../../core/util/types';
import type { ImportResult } from '../../formats/types';
import { m } from './import.messages';

export type { MergeAlign };
export { MERGE_ALIGNS };

/** `settings.ext` key of the remembered placement. */
export const MERGE_ALIGN_KEY = 'import.mergeAlign';

export function readMergeAlign(settings: Pick<SettingsService, 'getExt'>): MergeAlign {
  const v = settings.getExt(MERGE_ALIGN_KEY);
  return typeof v === 'string' && (MERGE_ALIGNS as readonly string[]).includes(v) ? (v as MergeAlign) : 'auto';
}

export function writeMergeAlign(settings: Pick<SettingsService, 'setExt'>, align: MergeAlign): void {
  settings.setExt(MERGE_ALIGN_KEY, align);
}

/** Files the merged lines need: line textures and custom hit sounds. */
export function neededAssets(doc: ChartDoc): Set<AssetId> {
  const out = new Set<AssetId>();
  for (const line of doc.lines.values()) if (line.texture !== null) out.add(line.texture);
  for (const note of doc.notes.values()) if (note.hitsound?.kind === 'asset') out.add(note.hitsound.asset);
  return out;
}

export type MergeOutcome =
  | { readonly ok: true; readonly lines: number; readonly snapped: number; readonly notes: readonly MessageRef[] }
  | { readonly ok: false; readonly reason: MessageRef };

/** Adds the imported chart into the open project (see the module header). */
export async function mergeIntoProject(
  services: Pick<AppServices, 'store' | 'persist' | 'i18n'>,
  result: Extract<ImportResult, { readonly kind: 'chart' }>,
  beatOffset: Beat,
  align: MergeAlign,
): Promise<MergeOutcome> {
  const store = services.store;
  if (store === null) return { ok: false, reason: { key: m.mergeNoProject } };
  const name = result.report.fileName;
  try {
    await services.persist.checkpoint(store.projectId, services.i18n.t(m.versionBeforeImport, { name }), 'import');
  } catch {
    // A missing safety version does not stop the merge: it is undoable anyway.
  }
  const needed = neededAssets(result.doc);
  try {
    for (const a of result.assets) {
      if (!needed.has(a.id) || store.doc.assets.has(a.id)) continue;
      await services.persist.putAsset(a.bytes, a.mime, a.role, a.meta);
    }
  } catch (e) {
    const error = e instanceof Error ? e.message : String(e);
    return { ok: false, reason: { key: m.createFailed, params: { error } } };
  }
  const out = store.transact({ label: { key: m.txnMerge, params: { name } }, command: 'file.import' }, (tx) =>
    Core.merge.mergeChart(tx, { source: result.doc, lines: 'all', beatOffset, align }),
  );
  if (!out.ok) return { ok: false, reason: out.reason };
  return { ok: true, lines: out.value.lines.length, snapped: out.value.snapped, notes: out.notes };
}
